<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import AccountSettingsForm from 'dashboard/components/ai-reply/AccountSettingsForm.vue';
import AgentPreference from 'dashboard/routes/settings/aiReply/AgentPreference.vue';
import aiReplySettingsAPI from 'dashboard/api/aiReplySettings';

const { t } = useI18n();
const { checkPermissions } = usePolicy();

const canManageAccountSettings = computed(() =>
  checkPermissions(['administrator', 'agent', 'custom_role'])
);
const canManagePreference = computed(() =>
  checkPermissions(['administrator', 'agent', 'custom_role'])
);

const isLoading = ref(true);
const isSaving = ref(false);
const isTesting = ref(false);
const testingMode = ref('auto_reply');
const settings = ref({
  enabled: false,
  aiAgentId: null,
  autoReplyApiEndpoint: '',
  autoReplyApiKey: '',
  autoReplyLkeBotBizId: '',
  suggestionApiEndpoint: '',
  suggestionApiKey: '',
  suggestionLkeBotBizId: '',
  humanHandoffPromptEnabled: true,
  timeoutSeconds: 10,
  retryCount: 0,
  eligibleMessageTypes: [],
  autoReplyLastTestStatus: null,
  autoReplyLastTestedAt: null,
  suggestionLastTestStatus: null,
  suggestionLastTestedAt: null,
});

const normalizeSettings = data => ({
  enabled: data.enabled ?? false,
  aiAgentId: data.ai_agent_id ?? null,
  autoReplyApiEndpoint: data.auto_reply_api_endpoint ?? '',
  autoReplyApiKey: '',
  autoReplyLkeBotBizId: data.auto_reply_lke_bot_biz_id ?? '',
  suggestionApiEndpoint: data.suggestion_api_endpoint ?? '',
  suggestionApiKey: '',
  suggestionLkeBotBizId: data.suggestion_lke_bot_biz_id ?? '',
  humanHandoffPromptEnabled: data.human_handoff_prompt_enabled ?? true,
  timeoutSeconds: data.timeout_seconds ?? 10,
  retryCount: data.retry_count ?? 0,
  eligibleMessageTypes: data.eligible_message_types ?? [],
  autoReplyLastTestStatus: data.auto_reply_last_test_status ?? null,
  autoReplyLastTestedAt: data.auto_reply_last_tested_at ?? null,
  suggestionLastTestStatus: data.suggestion_last_test_status ?? null,
  suggestionLastTestedAt: data.suggestion_last_tested_at ?? null,
});

const buildPayload = formData => ({
  enabled: formData.enabled,
  ai_agent_id: formData.aiAgentId,
  auto_reply_api_endpoint: formData.autoReplyApiEndpoint,
  auto_reply_api_key: formData.autoReplyApiKey,
  auto_reply_lke_bot_biz_id: formData.autoReplyLkeBotBizId,
  suggestion_api_endpoint: formData.suggestionApiEndpoint,
  suggestion_api_key: formData.suggestionApiKey,
  suggestion_lke_bot_biz_id: formData.suggestionLkeBotBizId,
  human_handoff_prompt_enabled: formData.humanHandoffPromptEnabled,
  timeout_seconds: formData.timeoutSeconds,
  retry_count: formData.retryCount,
  eligible_message_types: formData.eligibleMessageTypes,
});

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await aiReplySettingsAPI.get();
    settings.value = normalizeSettings(data);
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('AI_REPLY_SETTINGS.MESSAGES.LOAD_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

const handleSave = async formData => {
  isSaving.value = true;
  try {
    const { data } = await aiReplySettingsAPI.update(buildPayload(formData));
    settings.value = normalizeSettings(data);
    useAlert(t('AI_REPLY_SETTINGS.MESSAGES.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('AI_REPLY_SETTINGS.MESSAGES.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const handleTestConnection = async mode => {
  isTesting.value = true;
  testingMode.value = mode;
  try {
    const { data } = await aiReplySettingsAPI.testConnection(mode);
    const isSuccess = data.status === 'success';
    useAlert(
      data.message ||
        t(
          isSuccess
            ? 'AI_REPLY_SETTINGS.MESSAGES.TEST_SUCCESS'
            : 'AI_REPLY_SETTINGS.MESSAGES.TEST_FAILED'
        )
    );

    const timestamp = new Date().toISOString();
    if (mode === 'suggestion') {
      settings.value = {
        ...settings.value,
        suggestionLastTestStatus: data.status,
        suggestionLastTestedAt: timestamp,
      };
      return;
    }

    settings.value = {
      ...settings.value,
      autoReplyLastTestStatus: data.status,
      autoReplyLastTestedAt: timestamp,
    };
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('AI_REPLY_SETTINGS.MESSAGES.TEST_FAILED')
    );
  } finally {
    isTesting.value = false;
  }
};

onMounted(() => {
  if (!canManageAccountSettings.value) {
    isLoading.value = false;
    return;
  }

  fetchSettings();
});
</script>

<template>
  <div class="grid py-16 px-5 font-inter mx-auto gap-10 sm:max-w-screen-md">
    <div class="flex flex-col gap-2">
      <h2 class="text-2xl font-medium text-n-slate-12">
        {{ $t('AI_REPLY_SETTINGS.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('AI_REPLY_SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="canManageAccountSettings" class="grid gap-6">
      <div class="flex flex-col gap-2">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ $t('AI_REPLY_SETTINGS.SECTIONS.ACCOUNT_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('AI_REPLY_SETTINGS.SECTIONS.ACCOUNT_DESCRIPTION') }}
        </p>
      </div>

      <div v-if="isLoading" class="text-sm text-n-slate-11">
        {{ $t('AI_REPLY_SETTINGS.LOADING') }}
      </div>

      <AccountSettingsForm
        v-else
        :settings="settings"
        :is-saving="isSaving"
        :is-testing="isTesting"
        :testing-mode="testingMode"
        @save="handleSave"
        @test-connection="handleTestConnection"
      />
    </div>

    <div v-if="canManagePreference" class="grid gap-6">
      <div class="flex flex-col gap-2">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ $t('AI_REPLY_PREFERENCE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('AI_REPLY_PREFERENCE.DESCRIPTION') }}
        </p>
      </div>

      <AgentPreference embedded />
    </div>
  </div>
</template>
